/**
 * Estimates / quotes (tracker item 39): labels, status colours, which office
 * action a status allows (the backend's rules), form line drafts and error
 * texts. Pure helpers shared by the office, print, portal and public pages.
 */
import * as types from '../../api/types';
import { getErrorCode, getErrorDetails, getErrorMessage } from '../../utils/apiError';

export const ESTIMATES_PATH = '/estimates';
export const estimatePath = (id: string) => `${ESTIMATES_PATH}/${encodeURIComponent(id)}`;
export const estimateEditPath = (id: string) => `${estimatePath(id)}/edit`;
export const estimatePrintPath = (id: string) => `/print/estimates/${encodeURIComponent(id)}`;

export const ESTIMATE_STATUS_LABELS: Record<types.EstimateStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  CONVERTED: 'Converted',
  CANCELLED: 'Cancelled',
};

/** What the customer reads for a status (portal and public link). */
export const CUSTOMER_STATUS_LABELS: Record<types.EstimateStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Waiting for your answer',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  CONVERTED: 'Approved and scheduled',
  CANCELLED: 'Withdrawn',
};

export function estimateStatusClass(status: types.EstimateStatus): string {
  switch (status) {
    case 'DRAFT': return 'bg-gray-100 text-gray-700';
    case 'SENT': return 'bg-blue-100 text-blue-800';
    case 'APPROVED': return 'bg-green-100 text-green-800';
    case 'CONVERTED': return 'bg-emerald-100 text-emerald-800';
    case 'DECLINED': return 'bg-red-100 text-red-800';
    case 'EXPIRED': return 'bg-amber-100 text-amber-800';
    case 'CANCELLED': return 'bg-gray-200 text-gray-600';
    default: return 'bg-gray-100 text-gray-700';
  }
}

export const RESPONSE_CHANNEL_LABELS: Record<types.EstimateResponseChannel, string> = {
  PORTAL: 'the customer portal',
  LINK: 'the emailed link',
  OFFICE: 'the office',
};

/** The office actions a status allows (same rules as the backend; the server still decides). */
export function estimateActions(status: types.EstimateStatus): {
  edit: boolean;
  send: boolean;
  resend: boolean;
  record: boolean;
  cancel: boolean;
  convert: boolean;
} {
  return {
    edit: status === 'DRAFT',
    send: status === 'DRAFT',
    resend: status === 'SENT' || status === 'EXPIRED',
    record: status === 'SENT',
    cancel: status === 'DRAFT' || status === 'SENT' || status === 'EXPIRED' || status === 'APPROVED',
    convert: status === 'APPROVED',
  };
}

/** The bundle a form line belongs to (tracker item 85). Sent back on every save: PATCH lineItems replaces all lines. */
export interface DraftBundle {
  /** null = the bundle was deleted since; the name below is the snapshot */
  bundleId: string | null;
  bundleName: string;
  /** Days after the bundle's first visit */
  offsetDays: number;
  /** The date the bundle starts; null = asked at conversion */
  startDate: string | null;
}

/** One editable line of the estimate form. Prices are typed as text; a blank price = the catalog / chart price. */
export interface EstimateLineDraft {
  key: string;
  itemId: string;
  quantity: string;
  unitPrice: string;
  description: string;
  /** The stored line this draft came from (used to tell new bundle lines apart) */
  lineId?: string;
  bundle?: DraftBundle | null;
}

let draftCounter = 0;
export function newLineDraft(partial: Partial<EstimateLineDraft> = {}): EstimateLineDraft {
  draftCounter += 1;
  return { key: `line-${draftCounter}`, itemId: '', quantity: '1', unitPrice: '', description: '', ...partial };
}

/**
 * A stored line as a draft: a MANUAL price stays typed in; a catalog / chart
 * price is left blank so saving prices it again; a description equal to the
 * item name is left blank (it is the default).
 */
export function lineToDraft(line: types.EstimateLine): EstimateLineDraft {
  return newLineDraft({
    itemId: line.itemId,
    quantity: String(Number(line.quantity)),
    unitPrice: line.priceSource === 'MANUAL' ? line.unitPrice : '',
    description: line.description === line.itemName ? '' : line.description,
    lineId: line.id,
    bundle: line.bundleOffsetDays === null || line.bundleOffsetDays === undefined
      ? null
      : { bundleId: line.bundleId ?? null, bundleName: line.bundleName ?? '', offsetDays: line.bundleOffsetDays, startDate: line.bundleStartDate ?? null },
  });
}

/** Problems with the drafts before anything is sent (the server checks everything again). */
export function lineDraftProblem(drafts: EstimateLineDraft[]): string | null {
  for (const [index, line] of drafts.entries()) {
    const n = index + 1;
    if (!line.itemId) return `Line ${n}: choose an item`;
    const quantity = Number(line.quantity);
    if (!line.quantity.trim() || !Number.isFinite(quantity) || quantity <= 0) return `Line ${n}: the quantity must be greater than 0`;
    if (line.unitPrice.trim() !== '') {
      const price = Number(line.unitPrice);
      if (!Number.isFinite(price) || price < 0) return `Line ${n}: the price must be 0 or more`;
    }
  }
  return null;
}

export function draftsToInput(drafts: EstimateLineDraft[]): types.EstimateLineInput[] {
  return drafts.map((line) => ({
    itemId: line.itemId,
    quantity: line.quantity.trim(),
    ...(line.unitPrice.trim() !== '' ? { unitPrice: line.unitPrice.trim() } : {}),
    ...(line.description.trim() !== '' ? { description: line.description.trim() } : {}),
    // A bundle line goes back with its group, or the save would drop the grouping (a deleted bundle: its name instead)
    ...(line.bundle
      ? {
          ...(line.bundle.bundleId ? { bundleId: line.bundle.bundleId } : { bundleName: line.bundle.bundleName }),
          bundleOffsetDays: line.bundle.offsetDays,
          bundleStartDate: line.bundle.startDate,
        }
      : {}),
  }));
}

// ------------------------------------------------------------ service bundles (tracker item 85)

/** `Day 45`: the planned visit as days after the bundle's first visit. */
export function bundleDayLabel(offsetDays: number | null | undefined): string {
  return `Day ${offsetDays ?? 0}`;
}

/** Stable key of a bundle group (a bundle deleted since is grouped by its name). */
export function bundleGroupKey(bundle: Pick<DraftBundle, 'bundleId' | 'bundleName'>): string {
  return bundle.bundleId ?? `name:${bundle.bundleName}`;
}

export interface DraftSection {
  /** null = the plain lines */
  bundle: DraftBundle | null;
  key: string;
  /** Indexes into the drafts, in order */
  indexes: number[];
}

/** The drafts split into the plain lines and one section per bundle, in order of first appearance. */
export function draftSections(drafts: EstimateLineDraft[]): DraftSection[] {
  const plain: DraftSection = { bundle: null, key: 'plain', indexes: [] };
  const bundles = new Map<string, DraftSection>();
  drafts.forEach((draft, index) => {
    if (!draft.bundle) { plain.indexes.push(index); return; }
    const key = bundleGroupKey(draft.bundle);
    const found = bundles.get(key) ?? { bundle: draft.bundle, key, indexes: [] };
    found.indexes.push(index);
    bundles.set(key, found);
  });
  return [...(plain.indexes.length ? [plain] : []), ...bundles.values()];
}

export interface GroupTotals {
  subtotal: string;
  tax: string;
  total: string;
}

/** A bundle's subtotal, tax and total: from the priced preview of its lines when there is one, else the server's group. */
export function bundleGroupTotals(indexes: number[], preview: types.EstimatePreview | null, group: types.EstimateBundleGroup | undefined): GroupTotals | null {
  const cents = (value: string) => Math.round(Number(value) * 100);
  const money = (total: number) => (total / 100).toFixed(2);
  if (preview && indexes.every((i) => preview.lineItems[i])) {
    const rows = indexes.map((i) => preview.lineItems[i]);
    return {
      subtotal: money(rows.reduce((sum, r) => sum + cents(r.lineAmount), 0)),
      tax: money(rows.reduce((sum, r) => sum + cents(r.lineTax), 0)),
      total: money(rows.reduce((sum, r) => sum + cents(r.lineTotal), 0)),
    };
  }
  return group ? { subtotal: group.subtotal, tax: group.totalTax, total: group.total } : null;
}

/** The names of the bundles on an estimate (list badge). */
export function estimateBundleNames(estimate: Pick<types.Estimate, 'bundles'> | { bundles?: types.EstimateBundleGroup[] }): string[] {
  return (estimate.bundles ?? []).map((b) => b.bundleName);
}

/** The bundles a 409 BUNDLE_START_DATE_REQUIRED names (details.bundles), or null for any other error. */
export function bundleStartRequired(error: unknown): { bundleId: string | null; bundleName: string }[] | null {
  if (getErrorCode(error) !== 'BUNDLE_START_DATE_REQUIRED') return null;
  const details = getErrorDetails(error);
  const list = details && Array.isArray(details.bundles) ? (details.bundles as unknown[]) : [];
  return list
    .filter((b): b is { bundleId?: unknown; bundleName?: unknown } => typeof b === 'object' && b !== null)
    .map((b) => ({ bundleId: typeof b.bundleId === 'string' ? b.bundleId : null, bundleName: typeof b.bundleName === 'string' ? b.bundleName : 'Bundle' }));
}

/** Today in the browser's calendar plus `days`, as YYYY-MM-DD. */
export function localDatePlus(days: number, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const ERROR_TEXT: Record<string, string> = {
  ESTIMATE_NOT_EDITABLE: 'Only a draft estimate can be edited. Reload to see its current state.',
  ESTIMATE_NOT_SENDABLE: 'This estimate can no longer be sent.',
  ESTIMATE_EMPTY: 'Add at least one line before sending the estimate.',
  ESTIMATE_EXPIRED: 'This estimate has expired.',
  ESTIMATE_ALREADY_ANSWERED: 'This estimate was already answered.',
  ESTIMATE_NOT_CANCELLABLE: 'This estimate can no longer be cancelled.',
  ESTIMATE_NOT_APPROVED: 'Only an approved estimate can be converted.',
  ESTIMATE_ALREADY_CONVERTED: 'This estimate was already converted.',
  ESTIMATE_CHANGED: 'The estimate changed meanwhile. Reload and try again.',
  RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
  BUNDLE_ALREADY_ON_ESTIMATE: 'This bundle is already on the estimate. Remove it first to add it again.',
  BUNDLE_EMPTY: 'None of the items in this bundle can be scheduled any more, so there is nothing to add.',
  BUNDLE_INACTIVE: 'This bundle is turned off. Turn it on in Settings > Service bundles to add it.',
  BUNDLE_START_DATE_REQUIRED: 'Choose the date the first visit of each bundle falls on.',
  ENROLLMENT_ITEM_UNAVAILABLE: 'An item in one of the bundles is no longer available, so nothing was created. Cancel this estimate and make a new one without that item.',
};

/** The message for a failed estimate action: a known code's text, else the server's message. */
export function estimateErrorText(error: unknown, fallback: string): string {
  const code = getErrorCode(error);
  if (code && ERROR_TEXT[code]) return ERROR_TEXT[code];
  return getErrorMessage(error, fallback);
}

/** Codes after which the page should reload the estimate (its status changed under us). */
export function isStaleEstimateError(error: unknown): boolean {
  const code = getErrorCode(error);
  return !!code && ['ESTIMATE_NOT_EDITABLE', 'ESTIMATE_NOT_SENDABLE', 'ESTIMATE_EXPIRED', 'ESTIMATE_ALREADY_ANSWERED',
    'ESTIMATE_NOT_CANCELLABLE', 'ESTIMATE_NOT_APPROVED', 'ESTIMATE_ALREADY_CONVERTED', 'ESTIMATE_CHANGED'].includes(code);
}

/** "123 Main St, Springfield, IL" */
export function estimateAddress(property: Pick<types.Estimate['property'], 'address' | 'city' | 'state'>): string {
  return [property.address, property.city, property.state].filter(Boolean).join(', ');
}

/** A short random key for the send button, so a double click queues one email. */
export function newIdempotencyKey(): string {
  return `send-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
